import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'

import {cleanResponse} from '../../lib/apiHelpers'
import apiV2 from '../../lib/apiV2'
import {base64ToBlob, canBrowserPlayAudio, getAudioMimeType, playedSeconds, resolveAudioDuration} from './audioUtils'
import {fileExtension} from '../forms/v2/lib/tools'

const AudioPlayerContext = createContext(null)

export const useAudioPlayer = () => useContext(AudioPlayerContext)

const LOAD_ERROR = {
  type: 'load',
  message: "We couldn't play this recording right now. Please try again in a moment."
}

const NETWORK_ERROR = {
  type: 'network',
  message: 'Playback was interrupted. Check your connection and press play to continue.'
}

const UNPLAYABLE_ERROR = {
  type: 'unplayable',
  message: "This recording appears to be damaged and can't be played. You can still try downloading it."
}

const BROWSER_ERROR = {
  type: 'browser',
  message: "This recording can't be played in your browser. Try a different browser, or download the file to listen."
}

const NOT_FOUND_ERROR = {
  type: 'not-found',
  message: 'This recording is no longer available.'
}

const PERMISSION_ERROR = {
  type: 'permission',
  message: "You don't have permission to play this recording."
}

const SESSION_ERROR = {
  type: 'session',
  message: 'Your session has timed out. Please sign in again to continue listening.'
}

const SERVER_ERROR = {
  type: 'load',
  message: 'Something went wrong on our end. Please try again shortly.'
}

const UNPLAYABLE_MEDIA_ERROR_CODES = [3, 4]

const errorFromPlayFailure = (caughtError, extension) => {
  if (!caughtError) return LOAD_ERROR

  if (caughtError.status === 401) return SESSION_ERROR
  if (caughtError.status === 403) return PERMISSION_ERROR
  if (caughtError.status === 404 || caughtError.status === 410) return NOT_FOUND_ERROR
  if (caughtError.status >= 500) return SERVER_ERROR

  if (caughtError.name === 'NotSupportedError')
    return canBrowserPlayAudio(extension) ? UNPLAYABLE_ERROR : BROWSER_ERROR

  return LOAD_ERROR
}

const decryptFile = id => new Promise((resolve, reject) => {
  apiV2.GET({
    id: `${id}?download=true`,
    onSuccess: response => {
      const formattedResponse = cleanResponse(response.body.data)
      const fileData = formattedResponse.get('decryptedFile')
      const fileName = formattedResponse.getIn(['attributes', 'filename'])

      resolve({fileData, fileName})
    },
    onFail: reject,
    resource: 'uploads'
  })
})

export const AudioPlayerProvider = ({children}) => {
  const audioRef = useRef(new Audio())
  const blobUrlRef = useRef(null)
  // Identity of the latest load: decryption can't be canceled, so a slower
  // earlier load must not start its recording over a newer track.
  const loadRequestRef = useRef(0)

  const [currentTrack, setCurrentTrack] = useState(null) // { id, name }
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [played, setPlayed] = useState(0) // seconds of the current track actually heard
  const [error, setError] = useState(null) // { type: 'load' | 'network', message }

  useEffect(() => {
    const audio = audioRef.current

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
      setPlayed(playedSeconds(audio))
    }
    const handleDurationChange = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration)
    }
    const handleEnded = () => {
      setIsPlaying(false)
      setPlayed(playedSeconds(audio))

      // Fallback: at the end of playback currentTime equals the true length
      if (!isFinite(audio.duration)) setDuration(audio.currentTime)
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleError = () => {
      if (audio.src) {
        setIsPlaying(false)
        setIsLoading(false)

        if (audio.error && UNPLAYABLE_MEDIA_ERROR_CODES.includes(audio.error.code))
          setError(UNPLAYABLE_ERROR)
        else
          setError(NETWORK_ERROR)
      }
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('error', handleError)
      audio.pause()
      audio.src = ''

      if (blobUrlRef.current)
        URL.revokeObjectURL(blobUrlRef.current)
    }
  }, [])

  const playTrack = useCallback(async (id, name) => {
    const audio = audioRef.current

    // If same track, toggle play/pause
    if (currentTrack && currentTrack.id === id) {
      if (isPlaying)
        audio.pause()
      else
        audio.play()

      return
    }

    // New track - load and play. The previous track stops now, or it keeps
    // playing (and counting as heard) under the new one while that loads.
    loadRequestRef.current += 1
    const requestId = loadRequestRef.current

    audio.pause()
    setIsLoading(true)
    setCurrentTrack({id, name})
    setCurrentTime(0)
    setDuration(0)
    setPlayed(0)
    setError(null)

    // Clean up previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }

    const extension = fileExtension(name || '')

    try {
      const {fileData} = await decryptFile(id)

      if (requestId !== loadRequestRef.current) return

      const mimeType = getAudioMimeType(extension)
      const blob = base64ToBlob(fileData, mimeType)
      const blobUrl = URL.createObjectURL(blob)

      blobUrlRef.current = blobUrl
      audio.src = blobUrl
      await resolveAudioDuration(audio)

      if (requestId !== loadRequestRef.current) return

      await audio.play()
    } catch (caughtError) {
      if (requestId !== loadRequestRef.current) return

      setIsPlaying(false)
      setError(errorFromPlayFailure(caughtError, extension))
    } finally {
      if (requestId === loadRequestRef.current) setIsLoading(false)
    }
  }, [currentTrack, isPlaying])

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current

    if (!currentTrack) return

    if (isPlaying)
      audio.pause()
    else
      audio.play()
  }, [currentTrack, isPlaying])

  const seek = useCallback(time => {
    audioRef.current.currentTime = time
  }, [])

  const stop = useCallback(() => {
    const audio = audioRef.current

    loadRequestRef.current += 1
    audio.pause()
    audio.currentTime = 0
    setCurrentTrack(null)
    setIsPlaying(false)
    setIsLoading(false)
    setCurrentTime(0)
    setDuration(0)
    setPlayed(0)
    setError(null)

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
  }, [])

  const retry = useCallback(async () => {
    if (!currentTrack) return

    const {id, name} = currentTrack
    const audio = audioRef.current

    // For network errors mid-playback, the blob is still loaded — just resume
    if (error && error.type === 'network' && blobUrlRef.current) {
      setIsLoading(true)
      try {
        await audio.play()
        setError(null)
      } catch {
        setError(NETWORK_ERROR)
      } finally {
        setIsLoading(false)
      }
      return
    }

    // For load errors, retry the full decryption and load
    loadRequestRef.current += 1
    const requestId = loadRequestRef.current

    setIsLoading(true)

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }

    const extension = fileExtension(name || '')

    try {
      const {fileData} = await decryptFile(id)

      if (requestId !== loadRequestRef.current) return

      const mimeType = getAudioMimeType(extension)
      const blob = base64ToBlob(fileData, mimeType)
      const blobUrl = URL.createObjectURL(blob)

      blobUrlRef.current = blobUrl
      audio.src = blobUrl
      await resolveAudioDuration(audio)

      if (requestId !== loadRequestRef.current) return

      await audio.play()
      setError(null)
    } catch (caughtError) {
      if (requestId !== loadRequestRef.current) return

      setIsPlaying(false)
      setError(errorFromPlayFailure(caughtError, extension))
    } finally {
      if (requestId === loadRequestRef.current) setIsLoading(false)
    }
  }, [currentTrack, error])

  const dismissError = useCallback(() => setError(null), [])

  const value = {
    currentTrack,
    currentTime,
    dismissError,
    duration,
    error,
    isLoading,
    isPlaying,
    played,
    playTrack,
    retry,
    seek,
    stop,
    togglePlayPause
  }

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  )
}

AudioPlayerProvider.propTypes = {
  children: PropTypes.node
}

export default AudioPlayerContext
